import React, { useState, useEffect, useRef } from 'react'
import { Play, Pause, RotateCcw, Settings } from 'lucide-react'

interface InteractiveOscillationProps {
    title?: string
    description?: string
    onProgress?: (progress: number) => void
    onComplete?: () => void
}

const InteractiveOscillation: React.FC<InteractiveOscillationProps> = ({
    title = "Mô phỏng dao động điều hòa",
    description = "Quan sát chuyển động dao động điều hòa và các đại lượng đặc trưng",
    onProgress,
    onComplete
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const animationRef = useRef<number>()

    const [isPlaying, setIsPlaying] = useState(false)
    const [time, setTime] = useState(0)
    const [amplitude, setAmplitude] = useState(100) // A - giá trị scale cho hiển thị (50-150)
    const [frequency, setFrequency] = useState(1) // f (Hz)
    const [phase, setPhase] = useState(0) // φ (rad)
    const [showVelocityVector, setShowVelocityVector] = useState(true)
    const [showGraph, setShowGraph] = useState(true)
    const [showSettings, setShowSettings] = useState(true)  // Hiển thị luôn để người dùng dễ sử dụng
    const hasCompletedRef = useRef(false) // ✅ Đảm bảo onComplete chỉ gọi 1 lần

    // Calculated values
    const omega = 2 * Math.PI * frequency // ω = 2πf
    const period = 1 / frequency // T = 1/f
    
    // Convert amplitude to cm for display (50-150 scale → 5-15 cm)
    const amplitudeCm = amplitude / 10

    // Current position and velocity
    const position = amplitude * Math.cos(omega * time + phase)
    const velocity = -amplitude * omega * Math.sin(omega * time + phase)
    const acceleration = -amplitude * omega * omega * Math.cos(omega * time + phase)

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // SUPER-SAMPLING: Gấp đôi devicePixelRatio để cực kỳ sắc nét
        const baseDpr = window.devicePixelRatio || 1
        const superSamplingFactor = 2
        const dpr = baseDpr * superSamplingFactor
        
        // Display size
        const displayWidth = 1000
        const displayHeight = 500
        
        // Actual buffer size
        const width = displayWidth * dpr
        const height = displayHeight * dpr
        
        // Set canvas buffer size
        canvas.width = width
        canvas.height = height
        
        // Normalize coordinate system
        ctx.scale(dpr, dpr)
        
        // Work with display coordinates
        const workWidth = displayWidth
        const workHeight = displayHeight
        
        // Clear canvas với nền trắng tinh khiết
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, workWidth, workHeight)
        
        // Enable maximum quality rendering
        ctx.imageSmoothingEnabled = true
        ctx.imageSmoothingQuality = 'high'
        ctx.textRendering = 'optimizeLegibility' as any

        const centerX = workWidth / 2
        const centerY = 140  // Đặt cố định gần đầu canvas (không còn text nên đẩy lên cao)
        const maxAmplitude = Math.min(workWidth, workHeight) / 4

        // Draw trục x ngang (làm nổi bật hơn)
        ctx.strokeStyle = '#000000'
        ctx.lineWidth = 3
        ctx.lineCap = 'round'
        ctx.beginPath()
        ctx.moveTo(60, centerY)
        ctx.lineTo(workWidth - 60, centerY)
        ctx.stroke()
        
        // Vẽ mũi tên cho trục x (ở đầu bên phải)
        const arrowX = workWidth - 60
        const arrowSize = 12
        ctx.fillStyle = '#000000'
        ctx.beginPath()
        ctx.moveTo(arrowX, centerY)
        ctx.lineTo(arrowX - arrowSize, centerY - arrowSize / 2)
        ctx.lineTo(arrowX - arrowSize, centerY + arrowSize / 2)
        ctx.closePath()
        ctx.fill()
        
        // Thêm nhãn trục x
        ctx.fillStyle = '#000000'
        ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        ctx.textAlign = 'right'
        ctx.fillText('x', workWidth - 40, centerY - 15)

        // BỎ đường vertical line - không cần thiết
        // (Đã loại bỏ đường thẳng đứng ở giữa)

        // Tính amplitude scale cho vị trí biên (amplitude từ 50-150, maxAmplitude là kích thước canvas)
        const amplitudeScale = (amplitude / 100) * maxAmplitude  // Normalize: 100 = maxAmplitude chuẩn
        
        // Draw amplitude limits với màu vàng đậm
        ctx.strokeStyle = '#f59e0b'
        ctx.lineWidth = 2.5
        ctx.setLineDash([8, 8])
        ctx.beginPath()
        ctx.moveTo(centerX - amplitudeScale, centerY)
        ctx.lineTo(centerX + amplitudeScale, centerY)
        ctx.stroke()
        ctx.setLineDash([])
        
        // Thêm đánh dấu vị trí biên -A và +A
        // Vạch dọc tại vị trí -A
        ctx.strokeStyle = '#f59e0b'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.moveTo(centerX - amplitudeScale, centerY - 15)
        ctx.lineTo(centerX - amplitudeScale, centerY + 15)
        ctx.stroke()
        
        // Vạch dọc tại vị trí +A
        ctx.beginPath()
        ctx.moveTo(centerX + amplitudeScale, centerY - 15)
        ctx.lineTo(centerX + amplitudeScale, centerY + 15)
        ctx.stroke()
        
        // Nhãn -A
        ctx.fillStyle = '#f59e0b'
        ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('-A', centerX - amplitudeScale, centerY - 25)
        
        // Nhãn +A
        ctx.fillText('+A', centerX + amplitudeScale, centerY - 25)
        
        // Đánh dấu vị trí cân bằng O bằng chấm tròn
        ctx.fillStyle = '#000000'
        ctx.beginPath()
        ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI)
        ctx.fill()
        
        // Viền trắng cho chấm O để nổi bật
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI)
        ctx.stroke()
        
        // Nhãn O tại tâm
        ctx.fillStyle = '#000000'
        ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('O', centerX, centerY + 30)

        // Draw oscillating object (sử dụng amplitudeScale đã tính ở trên)
        const currentX = centerX + (position / amplitude) * amplitudeScale

        // Draw object với shadow và size lớn hơn
        ctx.shadowColor = 'rgba(59, 130, 246, 0.4)'
        ctx.shadowBlur = 4
        ctx.fillStyle = '#2563eb'
        ctx.beginPath()
        ctx.arc(currentX, centerY, 16, 0, 2 * Math.PI)
        ctx.fill()
        
        // Reset shadow
        ctx.shadowColor = 'transparent'
        ctx.shadowBlur = 0

        // Add white border for better visibility
        ctx.strokeStyle = '#ffffff'
        ctx.lineWidth = 3
        ctx.beginPath()
        ctx.arc(currentX, centerY, 16, 0, 2 * Math.PI)
        ctx.stroke()
        
        // Add outer border
        ctx.strokeStyle = '#1d4ed8'
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.arc(currentX, centerY, 16, 0, 2 * Math.PI)
        ctx.stroke()

        // Draw velocity vector if enabled
        if (showVelocityVector) {
            const velocityScale = 0.5

            // Velocity vector (green) với line cực dày
            ctx.strokeStyle = '#059669'
            ctx.lineWidth = 6  // Tăng từ 4 lên 6 (+50%)
            ctx.lineCap = 'round'
            ctx.beginPath()
            ctx.moveTo(currentX, centerY)
            const endX = currentX + velocity * velocityScale
            ctx.lineTo(endX, centerY)
            ctx.stroke()

            // Draw arrow head (lớn hơn để cân đối với line dày)
            if (Math.abs(velocity) > 0.1) {
                const arrowSize = 14  // Tăng từ 10 lên 14
                const direction = velocity > 0 ? 1 : -1

                ctx.fillStyle = '#059669'
                ctx.beginPath()
                ctx.moveTo(endX, centerY)
                ctx.lineTo(endX - direction * arrowSize, centerY - arrowSize / 2)
                ctx.lineTo(endX - direction * arrowSize, centerY + arrowSize / 2)
                ctx.closePath()
                ctx.fill()
            }
        }

        // Draw position graph if enabled
        if (showGraph) {
            const graphY = workHeight - 180
            const graphWidth = workWidth - 120
            const graphHeight = 160  // Tăng thêm để dễ quan sát hơn

            // Graph background với border đậm
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(60, graphY - graphHeight / 2, graphWidth, graphHeight)
            ctx.strokeStyle = '#9ca3af'
            ctx.lineWidth = 2
            ctx.strokeRect(60, graphY - graphHeight / 2, graphWidth, graphHeight)

            // Draw grid lines
            ctx.strokeStyle = '#d1d5db' // darker grid lines
            ctx.lineWidth = 1.5
            for (let i = 1; i < 4; i++) {
                const y = graphY - graphHeight / 2 + (i * graphHeight / 4)
                ctx.beginPath()
                ctx.moveTo(60, y)
                ctx.lineTo(60 + graphWidth, y)
                ctx.stroke()
            }

            // Draw sine wave với màu đậm và line dày
            ctx.strokeStyle = '#2563eb'
            ctx.lineWidth = 3
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
            ctx.beginPath()

            const timeWindow = 4 * period // Show 4 periods
            // CHỈ vẽ phần đã chạy qua (phía SAU điểm) — không hiện đường sin phía trước
            const xMax = ((time % timeWindow) / timeWindow) * graphWidth
            for (let x = 0; x <= xMax; x += 0.5) {
                const t = (x / graphWidth) * timeWindow
                const y = amplitude * Math.cos(omega * t + phase)
                const canvasX = 60 + x
                const canvasY = graphY - (y / amplitude) * (graphHeight / 2) * 0.95  // Tăng scale từ 0.85 lên 0.95

                if (x === 0) {
                    ctx.moveTo(canvasX, canvasY)
                } else {
                    ctx.lineTo(canvasX, canvasY)
                }
            }
            ctx.stroke()

            // Draw current time marker and position
            const currentTimeX = 60 + (time % timeWindow) / timeWindow * graphWidth
            const currentGraphY = graphY - (position / amplitude) * (graphHeight / 2) * 0.95  // Tăng scale từ 0.85 lên 0.95

            // Current position dot on graph - lớn hơn và rõ hơn
            ctx.fillStyle = '#ef4444'
            ctx.shadowColor = 'rgba(239, 68, 68, 0.4)'
            ctx.shadowBlur = 3
            ctx.beginPath()
            ctx.arc(currentTimeX, currentGraphY, 8, 0, 2 * Math.PI)
            ctx.fill()
            
            // Reset shadow
            ctx.shadowColor = 'transparent'
            ctx.shadowBlur = 0

            // Add white border
            ctx.strokeStyle = '#ffffff'
            ctx.lineWidth = 2.5
            ctx.beginPath()
            ctx.arc(currentTimeX, currentGraphY, 8, 0, 2 * Math.PI)
            ctx.stroke()
            
            // Add outer red border
            ctx.strokeStyle = '#dc2626'
            ctx.lineWidth = 2
            ctx.beginPath()
            ctx.arc(currentTimeX, currentGraphY, 8, 0, 2 * Math.PI)
            ctx.stroke()

            // Graph labels với font system và size lớn hơn
            ctx.fillStyle = '#374151'
            ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            ctx.fillText('x(t)', 65, graphY - graphHeight / 2 - 8)
            ctx.fillText('t', 60 + graphWidth + 8, graphY + 6)
            ctx.fillText('0', 50, graphY + 5)
            ctx.fillText('+A', 35, graphY - graphHeight / 2 * 0.95 + 5)
            ctx.fillText('-A', 35, graphY + graphHeight / 2 * 0.95 + 5)
        }

        // BỎ text hiển thị A, f, φ trên canvas vì đã có trên thanh trượt
        // Không cần hiển thị trùng lặp

    }, [time, amplitude, frequency, phase, showVelocityVector, showGraph, position, velocity, acceleration, omega, period])

    useEffect(() => {
        if (isPlaying) {
            const animate = () => {
                setTime(prevTime => {
                    const newTime = prevTime + 0.02
                    // ✅ Tự động mở khóa sau khi quan sát ít nhất 1.5 chu kỳ (CHỈ GỌI 1 LẦN)
                    if (newTime > 1.5 * period && onComplete && !hasCompletedRef.current) {
                        hasCompletedRef.current = true
                        onComplete()
                    }
                    // Report progress
                    if (onProgress) {
                        onProgress(Math.min(newTime / (2 * period), 1))
                    }
                    return newTime
                })
                animationRef.current = requestAnimationFrame(animate)
            }
            animationRef.current = requestAnimationFrame(animate)
        } else {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
            }
        }

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current)
            }
        }
    }, [isPlaying, period, onProgress, onComplete])

    const handlePlayPause = () => {
        setIsPlaying(!isPlaying)
        onProgress?.(time / (4 * period))
    }

    const handleReset = () => {
        setIsPlaying(false)
        setTime(0)
        onProgress?.(0)
    }

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6 my-6">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
                <p className="text-sm text-gray-600">{description}</p>
            </div>

            {/* Settings Panel - Hiển thị luôn, tối ưu cho mobile */}
            <div className="bg-blue-50 rounded-lg p-5 mb-4 space-y-4 border-2 border-blue-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-base md:text-sm font-semibold text-gray-800 mb-3">
                                Biên độ A: <span className="text-blue-600 text-lg">{amplitudeCm.toFixed(1)} cm</span>
                            </label>
                            <input
                                type="range"
                                min="50"
                                max="150"
                                step="5"
                                value={amplitude}
                                onChange={(e) => setAmplitude(Number(e.target.value))}
                                className="w-full h-8 md:h-6"
                            />
                            <div className="flex justify-between text-xs text-gray-500 mt-1">
                                <span>5 cm</span>
                                <span>15 cm</span>
                            </div>
                        </div>

                        <div>
                            <label className="block text-base md:text-sm font-semibold text-gray-800 mb-3">
                                Tần số f: <span className="text-blue-600 text-lg">{frequency.toFixed(1)} Hz</span>
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="3"
                                step="0.1"
                                value={frequency}
                                onChange={(e) => setFrequency(Number(e.target.value))}
                                className="w-full h-8 md:h-6"
                            />
                        </div>

                        <div>
                            <label className="block text-base md:text-sm font-semibold text-gray-800 mb-3">
                                Pha ban đầu φ: <span className="text-blue-600 text-lg">{(phase * 180 / Math.PI).toFixed(0)}°</span>
                            </label>
                            <input
                                type="range"
                                min="0"
                                max={2 * Math.PI}
                                step="0.1"
                                value={phase}
                                onChange={(e) => setPhase(Number(e.target.value))}
                                className="w-full h-8 md:h-6"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showVelocityVector}
                                onChange={(e) => setShowVelocityVector(e.target.checked)}
                                className="rounded w-5 h-5"
                            />
                            <span className="text-base md:text-sm font-medium text-gray-700">Hiển thị vector vận tốc</span>
                        </label>

                        <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={showGraph}
                                onChange={(e) => setShowGraph(e.target.checked)}
                                className="rounded w-5 h-5"
                            />
                            <span className="text-base md:text-sm font-medium text-gray-700">Hiển thị đồ thị</span>
                        </label>
                    </div>
                </div>

            {/* Canvas */}
            <div className="mb-4 bg-white rounded-lg p-6 flex justify-center border-2 border-gray-200">
                <canvas
                    ref={canvasRef}
                    style={{ 
                        width: '1000px', 
                        height: '500px', 
                        maxWidth: '100%',
                        imageRendering: 'crisp-edges'
                    }}
                    className="border-2 border-gray-400 rounded-lg shadow-lg"
                />
            </div>

            {/* Controls - Tối ưu cho mobile */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
                <div className="flex items-center gap-3 w-full sm:w-auto">
                    <button
                        onClick={handlePlayPause}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md active:scale-95"
                    >
                        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                        <span>{isPlaying ? 'Tạm dừng' : 'Phát'}</span>
                    </button>

                    <button
                        onClick={handleReset}
                        className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md active:scale-95"
                    >
                        <RotateCcw className="w-5 h-5" />
                        <span>Reset</span>
                    </button>
                </div>

                {/* Info Display - ẩn trên mobile nhỏ */}
                <div className="hidden sm:flex sm:flex-col text-sm text-gray-600 space-y-1 text-right">
                    <div className="font-medium">Chu kỳ T = {period.toFixed(2)} s</div>
                    <div className="font-medium">Tần số góc ω = {omega.toFixed(2)} rad/s</div>
                </div>
            </div>

            {/* Legend and Explanation */}
            <div className="mt-4 space-y-3">
                <div className="flex items-center justify-center space-x-6 text-sm">
                    <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-blue-600 rounded-full border-2 border-white"></div>
                        <span>Vật dao động</span>
                    </div>
                    {showVelocityVector && (
                        <div className="flex items-center space-x-2">
                            <div className="flex items-center">
                                <div className="w-6 h-0.5 bg-green-500"></div>
                                <div className="w-0 h-0 border-l-2 border-l-green-500 border-t-1 border-t-transparent border-b-1 border-b-transparent ml-0.5"></div>
                            </div>
                            <span>Vector vận tốc</span>
                        </div>
                    )}
                    {showGraph && (
                        <>
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-0.5 bg-blue-600"></div>
                                <span>Đồ thị x(t)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-white"></div>
                                <span>Vị trí hiện tại trên đồ thị</span>
                            </div>
                        </>
                    )}
                </div>

                <div className="text-center text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                    <p className="font-medium mb-1">💡 Cách quan sát:</p>
                    <p>• Vật dao động qua lại quanh vị trí cân bằng (giữa)</p>
                    <p>• Đồ thị bên dưới cho thấy li độ x thay đổi theo thời gian t</p>
                    <p>• Điểm đỏ trên đồ thị tương ứng với vị trí hiện tại của vật</p>
                    {showVelocityVector && <p>• Mũi tên xanh (vận tốc) lớn nhất ở giữa, bằng 0 ở biên</p>}
                </div>
            </div>
        </div>
    )
}

export default InteractiveOscillation